import React, {
  Children,
  Fragment,
  isValidElement,
  PropsWithChildren,
  ReactNode,
} from "react";
import Box from "../../layout/Box/Box.web";
import Flex, { type FlexProps } from "../../layout/Flex/Flex.web";

import type { CardSharedProps } from "./Card.props";

export default function Card({ children, ...props }: CardProps): JSX.Element {
  const [header, body] = extractSlots(children);

  return (
    <Box
      bg="bg"
      rounded={{ sm: "md" }}
      p={{ base: "24px 20px", sm: "xl" }}
      {...props}
    >
      <>
        {header}
        {body}
      </>
    </Box>
  );
}

Card.Header = function Header({ children, ...props }: CardProps): JSX.Element {
  return (
    <Box borderTopRadius="md" {...props}>
      {children}
    </Box>
  );
};

/**
 * Snugs up a footer to the bottom of a card with a border on top.
 * Perfect for attaching buttons for actions. Follows a similar
 * style to our `Modal.Footer` component.
 */
Card.Footer = function CardFooter(props: FlexProps) {
  return (
    <Flex
      py="2.5"
      px="md"
      mt="md"
      mx="-4"
      mb="-4"
      borderTop="1px solid"
      borderTopColor="border"
      roundedBottom={{ sm: "md" }}
      flexDirection="row-reverse"
      justifyContent="space-between"
      {...props}
    />
  );
};

export type CardProps = CardSharedProps & {
  as?: React.ElementType;
};

function extractSlots(
  children: CardProps["children"]
): [typeof Card.Header | undefined, ReactNode] {
  let header;
  const body: Array<ReactNode> = [];

  Children.forEach(children, (child: ReactNode) => {
    if (!isValidElement(child)) {
      body.push(child);
    } else if (child.type === Fragment) {
      const { children } = child.props as PropsWithChildren<
        Record<string, unknown>
      >;

      if (children) {
        const [headerFragment, bodyFragment] = extractSlots(children);

        header = headerFragment;
        body.push(bodyFragment);
      }
    } else if (child.type === Card.Header) {
      header = child;
    } else {
      body.push(child);
    }
  });

  return [header, body];
}
